import type { Modifier } from '@popperjs/core';
import {
  useOrganizationScopes,
  setOrganizationScopeEnabled,
  loadOrganizationScopeSettings,
} from '@features/lifecycles/organization-scopes.hooks';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Box, Paper, Popper, Tooltip, Typography } from '@mui/material';
import { useForkRef } from '@mui/material/utils';
import { DotSeparator } from '@ui/DotSeparator';
import { useIsConfirmed } from '@client/hooks/use-confirm.hook';
import { Button } from '@ui/buttons/Button';
import { IconButton } from '@ui/buttons/IconButton';
import { useProjectScopes } from '@features/lifecycles/project-scopes.hooks';
import { ToggleIcon } from '@ui/ToggleIcon';
import { Icon } from '@ui/Icon';
import { useBuildingLifetime } from '@client/store/project/project.hook';
import { useHoverTooltipOverlayRoot } from '@client/hooks/use-overlay-hover-tooltips.hook';
import { KeyboardKeys } from '@shared/models/keyboard.model';
import {
  ALL_SBEF_GROUP_KEYS,
  getSbefScopeGroupPrefix,
  SBEF_GROUP_OPTIONS,
} from '@features/sbef/sbef-group-options';
import {
  setSelectedSbefGroups,
  useSelectedSbefGroups,
} from '@features/sbef/sbef-groups.store';
import { isSbefFilterNoop } from '@features/sbef/sbef-summary.helpers';
import { useSelectSbefGroup } from '@features/sbef/use-select-sbef-group.hook';
import { useSelectedLifecycles } from '@features/lifecycles/lifecycle-scope.hooks';
import {
  SCOPE_PRESETS,
  ScopePresetId,
} from '@features/lifecycles/scope-presets.data';
import {
  applyScopePreset,
  useApplyScopePreset,
} from '@features/lifecycles/scope-presets.hooks';
import {
  detachScopePreset,
  useActiveScopePreset,
} from '@features/scope/scope.store';
import { useScopeOverlayRequest } from '@features/scope/scope-overlay.store';
import {
  setConservativeGenericCarbon,
  useConservativeGenericCarbon,
} from '@features/results/conservative-generic-carbon.hook';
import { LifecycleControls } from './LifecycleControls';
import { LIFECYCLE_GROUPS } from '@features/lifecycles/lifecycle-groups.data';
import { PresetDropdown } from './PresetDropdown';
import { SaveScopeForm } from './SaveScopeForm';
import {
  useSavedScopes,
  loadSavedScopes,
  deleteSavedScope,
} from '@features/lifecycles/saved-scopes.hooks';
import {
  getAvailableSavedScopes,
  savedScopeToPreset,
} from '@features/lifecycles/saved-scopes.helpers';
import { ProjectTargetField } from './ProjectTargetField';
import { buildScopeSummaryParts } from './scope-summary.helpers';
import { usePhasesCardStyles } from './phases-card.styles';

const ALL_SCOPE_LIFECYCLES = LIFECYCLE_GROUPS.flatMap(
  ({ toggleableLifecycles }) => toggleableLifecycles,
);

type PhasesCardProps = {
  panelAlignment?: 'center' | 'left';
  /** Panel only. Another control, such as the report cover, opens it. */
  hideTrigger?: boolean;
  /** Overview scope does not edit any individual project's period or target. */
  context?: 'project' | 'overview';
  /** Keep the scope overlay above surrounding panels and outside scroll areas. */
  portal?: boolean;
};

/** Scope trigger and the complete preset, SBEF, lifecycle and carbon overlay. */
export const PhasesCard: React.FC<PhasesCardProps> = ({
  panelAlignment = 'center',
  hideTrigger = false,
  context = 'project',
  portal = false,
}) => {
  const { classes, cx } = usePhasesCardStyles();
  const anchorRef = useRef<HTMLDivElement>(null);
  const horizontalAnchor = useRef<
    | {
        referenceX: number;
        viewportWidth: number;
        x: number;
      }
    | undefined
  >(undefined);
  const portalModifiers = useMemo<Partial<Modifier<string, object>>[]>(
    () => [
      {
        name: 'offset',
        options: {
          offset: [
            panelAlignment === 'left' ? 12 : 0,
            panelAlignment === 'left' ? 8 : 0,
          ],
        },
      },
      { name: 'computeStyles', options: { adaptive: false } },
      {
        name: 'keepScopeLeftEdge',
        enabled: true,
        phase: 'afterMain',
        requires: ['popperOffsets', 'preventOverflow'],
        fn: ({ state }) => {
          const offsets = state.modifiersData.popperOffsets;
          if (!offsets) return;
          const referenceX = state.rects.reference.x;
          const viewportWidth =
            state.elements.popper.ownerDocument.documentElement.clientWidth;
          const previous = horizontalAnchor.current;
          // Re-anchor for a moved trigger or resized viewport, but not preview width changes.
          if (
            !previous ||
            previous.referenceX !== referenceX ||
            previous.viewportWidth !== viewportWidth
          ) {
            const sizer = state.elements.popper.querySelector(
              '[data-scope-width-sizer]',
            );
            const column = state.elements.popper.querySelector(
              '[data-scope-preset-column]',
            );
            const controls = state.elements.popper.querySelector(
              '[data-scope-controls]',
            );
            const controlsStyle = controls && getComputedStyle(controls);
            const fullWidth =
              sizer && column && controlsStyle
                ? sizer.getBoundingClientRect().width +
                  column.getBoundingClientRect().width +
                  parseFloat(controlsStyle.paddingLeft) +
                  parseFloat(controlsStyle.paddingRight)
                : state.rects.popper.width;
            horizontalAnchor.current = {
              referenceX,
              viewportWidth,
              x: Math.max(
                16,
                Math.min(offsets.x, viewportWidth - fullWidth - 16),
              ),
            };
          }
          offsets.x = horizontalAnchor.current?.x ?? offsets.x;
        },
      },
    ],
    [panelAlignment],
  );
  const selectedLifecycles = useSelectedLifecycles();
  const lifetime = useBuildingLifetime();
  const selectedSbefGroups = useSelectedSbefGroups();
  const selectSbefGroup = useSelectSbefGroup();
  const activePreset = useActiveScopePreset();
  const projectScopes = useProjectScopes();
  const organizationScopes = useOrganizationScopes();
  const enabledIds =
    context === 'overview'
      ? organizationScopes.enabledIds
      : projectScopes.enabledIds;
  const readonly = context === 'project' && projectScopes.readonly;
  const [editingScopes, setEditingScopes] = useState(false);
  const {
    scopes: savedScopes,
    organization,
    canSave,
    loadError,
  } = useSavedScopes();
  const confirm = useIsConfirmed();
  const [confirmingScopeAction, setConfirmingScopeAction] = useState(false);
  const [deletingScopeId, setDeletingScopeId] = useState<string>();
  const [deleteError, setDeleteError] = useState(false);
  const presets = useMemo(
    () => [
      ...SCOPE_PRESETS,
      ...getAvailableSavedScopes(
        savedScopes,
        context === 'project' ? enabledIds : [],
        context === 'project' ? activePreset?.id : undefined,
      ).map(savedScopeToPreset),
    ],
    [savedScopes, enabledIds, context, activePreset?.id],
  );
  const visiblePresets = editingScopes
    ? presets
    : presets.filter(({ id }) => enabledIds.includes(id));
  useEffect(() => {
    const retired = savedScopes.some(
      (scope) => scope.deleted && `saved:${scope.id}` === activePreset?.id,
    );
    if (retired && context === 'overview') detachScopePreset();
  }, [savedScopes, activePreset, context]);
  const applyPreset = useApplyScopePreset();
  const conservativeGenericCarbon = useConservativeGenericCarbon();
  const [panelOpen, setPanelOpen] = useState(false);
  const [focusTargetRequestId, setFocusTargetRequestId] = useState(0);
  const overlayRootRef = useHoverTooltipOverlayRoot(panelOpen);
  const panelRef = useRef<HTMLDivElement>(null);
  const handlePanelRef = useForkRef(panelRef, overlayRootRef);
  const [previewPresetId, setPreviewPresetId] = useState<ScopePresetId | null>(
    null,
  );
  const previewPreset = presets.find(({ id }) => id === previewPresetId);
  const displayedSbefGroups = previewPreset?.sbefGroups ?? selectedSbefGroups;
  const displayedConservativeGenericCarbon = previewPreset
    ? previewPreset.genericClimateData === 'conservative'
    : conservativeGenericCarbon;
  const sbefFilterActive = !isSbefFilterNoop(selectedSbefGroups);

  const scopeSummary = useMemo(
    () =>
      buildScopeSummaryParts(
        selectedLifecycles,
        context === 'project' ? lifetime : undefined,
        selectedSbefGroups,
        sbefFilterActive,
      ),
    [
      selectedLifecycles,
      lifetime,
      selectedSbefGroups,
      sbefFilterActive,
      context,
    ],
  );

  const isPlaceholder = activePreset === undefined;
  const displayLabel = activePreset?.label ?? scopeSummary.lifecycle;
  const triggerLabel = isPlaceholder
    ? displayLabel
    : displayLabel.toUpperCase();

  const handleApplyPreset = useCallback(
    (presetId: ScopePresetId) => {
      const preset = presets.find(({ id }) => id === presetId);
      if (!preset) return;
      const savedOrganization = preset.id.startsWith('saved:')
        ? organization
        : undefined;
      if (context === 'overview') applyScopePreset(preset, savedOrganization);
      else applyPreset(preset, savedOrganization);
      setPreviewPresetId(null);
    },
    [applyPreset, context, presets, organization],
  );

  const setEnabled = useCallback(
    (id: string, enabled: boolean) => {
      // Disabling the active scope switches to the first remaining enabled one,
      // in enabled-list order like getFirstEnabledScope.
      const fallbackId =
        !enabled && id === activePreset?.id
          ? enabledIds.find((scopeId) => scopeId !== id)
          : undefined;
      const fallback = presets.find((preset) => preset.id === fallbackId);
      if (fallback) {
        applyScopePreset(
          fallback,
          fallback.id.startsWith('saved:') ? organization : undefined,
        );
        setPreviewPresetId(null);
      }

      // The fallback's period is saved with the visibility change in one snapshot.
      if (context === 'project')
        projectScopes.setEnabled(id, enabled, fallback?.calculationPeriodYears);
      else if (organizationScopes.organization)
        void setOrganizationScopeEnabled(
          organizationScopes.organization,
          id,
          enabled,
        );
    },
    [
      activePreset?.id,
      presets,
      enabledIds,
      organization,
      context,
      projectScopes,
      organizationScopes.organization,
    ],
  );

  const handleDeleteScope = useCallback(
    async (id: string) => {
      if (!organization || deletingScopeId) return;
      setConfirmingScopeAction(true);
      let confirmed: boolean;
      try {
        confirmed = await confirm({
          title: 'Delete from organisation?',
          description: 'This scope will not be available in new projects',
          cancellationText: 'Cancel',
          confirmationText: 'Delete',
        });
      } finally {
        setConfirmingScopeAction(false);
      }
      if (!confirmed) return;
      setDeletingScopeId(id);
      setDeleteError(false);
      try {
        await deleteSavedScope(organization, id);
        // Issue 4: After deletion, disable the scope immediately to remove it from the panel
        const scopeId = `saved:${id}`;
        if (enabledIds.includes(scopeId)) {
          setEnabled(scopeId, false);
        }
      } catch {
        setDeleteError(true);
      } finally {
        setDeletingScopeId(undefined);
      }
    },
    [organization, deletingScopeId, confirm, enabledIds, setEnabled],
  );

  const handleToggleAllSbef = useCallback(() => {
    const newGroups =
      selectedSbefGroups.length === ALL_SBEF_GROUP_KEYS.length
        ? []
        : [...ALL_SBEF_GROUP_KEYS];
    setSelectedSbefGroups(newGroups);
  }, [selectedSbefGroups]);

  const closePanel = useCallback(() => {
    if (confirmingScopeAction) return;
    if (selectedSbefGroups.length === 0) {
      setSelectedSbefGroups([...ALL_SBEF_GROUP_KEYS]);
    }
    setPreviewPresetId(null);
    setPanelOpen(false);
    horizontalAnchor.current = undefined;
    setEditingScopes(false);
    // The request to open on the target field belonged to the panel that just
    // closed. Leaving it set would hand the field focus every later time the
    // panel opens, since the field is mounted anew with each open.
    setFocusTargetRequestId(0);
  }, [selectedSbefGroups, confirmingScopeAction]);

  useEffect(() => {
    if (!panelOpen) return;
    const handleOutsideClick = (event: MouseEvent) => {
      const path = event.composedPath();
      if (
        (anchorRef.current && path.includes(anchorRef.current)) ||
        (panelRef.current && path.includes(panelRef.current)) ||
        path.some(
          (node) =>
            node instanceof Element &&
            (node.getAttribute('role') === 'dialog' ||
              // Menus opened from the panel, like the target level menu. The
              // filter menu holding the trigger is outside and closes it.
              (node.getAttribute('data-type') === 'menu' &&
                !node.contains(anchorRef.current))),
        )
      )
        return;
      closePanel();
    };
    // The containing filter menu can stop bubbling clicks before they reach document.
    document.addEventListener('click', handleOutsideClick, true);
    return () =>
      document.removeEventListener('click', handleOutsideClick, true);
  }, [panelOpen, closePanel]);

  // Opened from elsewhere — today the target line in the proposals chart.
  const { openRequestId, focusTarget } = useScopeOverlayRequest();
  const handledOpenRequestRef = useRef(openRequestId);
  useEffect(() => {
    if (openRequestId === handledOpenRequestRef.current) return;
    handledOpenRequestRef.current = openRequestId;
    setPanelOpen(true);
    if (focusTarget) setFocusTargetRequestId(openRequestId);
  }, [openRequestId, focusTarget]);

  // Escape closes the overlay, but only when nothing in it is being typed in:
  // the target field and the lifetime editor take Escape to cancel their own
  // edit, and closing the panel out from under them would swallow that.
  useEffect(() => {
    if (!panelOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== KeyboardKeys.ESCAPE) return;
      const active = document.activeElement;
      const isEditing =
        active instanceof HTMLElement &&
        active.closest('input, textarea, [contenteditable="true"]') !== null;
      if (isEditing) return;
      closePanel();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [panelOpen, closePanel]);

  const handleTogglePanel = useCallback(() => {
    if (panelOpen) closePanel();
    else setPanelOpen(true);
  }, [panelOpen, closePanel]);

  const panel = (
    <Paper
      ref={handlePanelRef}
      className={cx(
        classes.groupsPanel,
        panelAlignment === 'left' && classes.groupsPanelLeft,
      )}
      style={
        portal
          ? {
              position: 'relative',
              top: 'auto',
              left: 'auto',
              transform: 'none',
            }
          : undefined
      }
      elevation={2}
    >
      <Box
        component="button"
        type="button"
        className={classes.closeButton}
        aria-label="Close"
        onClick={closePanel}
      >
        <Icon category="mui" icon="Close" size={13} />
      </Box>
      <Box className={classes.presetColumn} data-scope-preset-column>
        <Box className={classes.presetColumnSizer} aria-hidden>
          {presets.map((preset) => (
            <Box key={preset.id} className={classes.presetSizerLabel}>
              {preset.label}
            </Box>
          ))}
        </Box>
        <Box className={classes.presetHeading}>
          <Typography variant="h6">Calculation scope</Typography>
          {canSave &&
            !readonly &&
            (editingScopes ? (
              <Button
                onClick={() => {
                  setEditingScopes(false);
                  setPreviewPresetId(null);
                }}
              >
                Done
              </Button>
            ) : (
              <Tooltip title="Edit scopes">
                <IconButton
                  category="mui"
                  icon="EditOutlined"
                  size={14}
                  aria-label="Edit scopes"
                  onClick={() => {
                    setEditingScopes(true);
                    setPreviewPresetId(null);
                  }}
                />
              </Tooltip>
            ))}
        </Box>
        {/* The preview is cleared when the pointer leaves the list, not a
                row: the gaps between rows would otherwise flick the controls
                back to the current scope on the way to the next preset. */}
        <Box
          className={classes.presetList}
          onMouseLeave={() => setPreviewPresetId(null)}
        >
          {visiblePresets.map((preset) => {
            const enabled = enabledIds.includes(preset.id);
            const selected = preset.id === activePreset?.id;
            return (
              <Box
                key={preset.id}
                className={classes.presetRow}
                onMouseEnter={() => setPreviewPresetId(preset.id)}
              >
                <Box
                  component="button"
                  type="button"
                  className={cx(
                    classes.presetOption,
                    selected && classes.presetOptionSelected,
                  )}
                  data-toggle-control={editingScopes || undefined}
                  role={editingScopes ? 'switch' : undefined}
                  aria-checked={editingScopes ? enabled : undefined}
                  aria-label={
                    editingScopes
                      ? `Show ${preset.label} in ${context === 'overview' ? 'organisation' : 'project'}`
                      : undefined
                  }
                  aria-pressed={editingScopes ? undefined : selected}
                  onClick={() => {
                    if (editingScopes) setEnabled(preset.id, !enabled);
                    else handleApplyPreset(preset.id);
                  }}
                >
                  <Box
                    className={cx(
                      classes.presetCheck,
                      (selected || editingScopes) && classes.presetCheckOn,
                    )}
                    aria-hidden
                  >
                    {editingScopes ? (
                      <ToggleIcon checked={enabled} />
                    ) : (
                      <Icon category="mui" icon="Check" size={18} />
                    )}
                  </Box>
                  <Typography
                    component="span"
                    className={classes.presetLabel}
                    data-label={preset.label}
                  >
                    <span className={classes.presetLabelText}>
                      {preset.label}
                    </span>
                  </Typography>
                </Box>
                {editingScopes &&
                  savedScopes.some(
                    (scope) =>
                      `saved:${scope.id}` === preset.id && !scope.deleted,
                  ) && (
                    <Tooltip title="Delete">
                      <IconButton
                        category="mui"
                        icon="Delete"
                        size={14}
                        aria-label={`Delete ${preset.label}`}
                        disabled={Boolean(deletingScopeId)}
                        onClick={() => {
                          void handleDeleteScope(
                            preset.id.slice('saved:'.length),
                          );
                        }}
                      />
                    </Tooltip>
                  )}
              </Box>
            );
          })}
        </Box>
        {deleteError && (
          <Typography role="alert" className={classes.savedScopesError}>
            Could not delete scope. Try again.
          </Typography>
        )}
        {(loadError ||
          (context === 'overview' && organizationScopes.error)) && (
          <Typography role="alert" className={classes.savedScopesError}>
            Could not load saved scopes.
            <Button
              onClick={() => {
                if (organization) {
                  void loadSavedScopes(organization);
                  if (context === 'overview')
                    void loadOrganizationScopeSettings(organization);
                }
              }}
            >
              Retry
            </Button>
          </Typography>
        )}
        {editingScopes && (
          <SaveScopeForm
            key={organization}
            context={context}
            onConfirmingChange={setConfirmingScopeAction}
            onSaved={(preset, savedOrganization) => {
              setEnabled(preset.id, true);
              applyScopePreset(preset, savedOrganization);
              setPreviewPresetId(null);
            }}
          />
        )}
      </Box>

      <Box className={classes.scopeControls} data-scope-controls>
        {portal && context === 'overview' && (
          <Box
            className={classes.widthSizer}
            data-scope-width-sizer
            aria-hidden
          >
            <LifecycleControls
              previewLifecycles={ALL_SCOPE_LIFECYCLES}
              allowLifetimeEditing={false}
            />
          </Box>
        )}
        <Box className={classes.controlSection}>
          <Typography className={classes.sectionLabel}>Lifecycle</Typography>
          <Box className={classes.lifecycleControls}>
            <LifecycleControls
              allowLifetimeEditing={context === 'project'}
              previewLifecycles={previewPreset?.lifecycles}
              previewLifetime={previewPreset?.calculationPeriodYears}
            />
          </Box>
        </Box>

        <Box className={classes.controlSection}>
          <Typography
            className={classes.sectionLabel}
            role="button"
            tabIndex={0}
            onClick={handleToggleAllSbef}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleToggleAllSbef();
              }
            }}
          >
            SBEF groups
          </Typography>
          <Box className={classes.sbefOptions}>
            {SBEF_GROUP_OPTIONS.map((option, index) => {
              const selected = displayedSbefGroups.includes(option.key);
              const previousKey = SBEF_GROUP_OPTIONS[index - 1]?.key;
              const nextKey = SBEF_GROUP_OPTIONS[index + 1]?.key;
              const connectsLeft =
                selected &&
                previousKey !== undefined &&
                displayedSbefGroups.includes(previousKey);
              const connectsRight =
                selected &&
                nextKey !== undefined &&
                displayedSbefGroups.includes(nextKey);
              return (
                <Tooltip
                  key={option.key}
                  title={option.description ?? option.label}
                  placement="bottom"
                  arrow
                >
                  <Box
                    component="button"
                    type="button"
                    className={cx(
                      classes.sbefOption,
                      selected && classes.sbefOptionSelected,
                      connectsLeft && classes.sbefOptionConnectedLeft,
                      connectsRight && classes.sbefOptionConnectedRight,
                    )}
                    aria-pressed={selected}
                    onClick={() => selectSbefGroup(option.key)}
                  >
                    {getSbefScopeGroupPrefix(option.key)}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>

        <Box className={classes.controlSection}>
          <Typography className={classes.sectionLabel}>
            Generic carbon factors
          </Typography>
          <Box className={classes.carbonOptions}>
            <Box
              component="button"
              type="button"
              className={cx(
                classes.carbonOption,
                !displayedConservativeGenericCarbon &&
                  classes.carbonOptionSelected,
              )}
              aria-pressed={!displayedConservativeGenericCarbon}
              onClick={() => setConservativeGenericCarbon(false)}
            >
              <Icon category="ui" icon="genericCarbonTypical" size={14} />
              <Typography component="span">Typical</Typography>
            </Box>
            <Box
              component="button"
              type="button"
              className={cx(
                classes.carbonOption,
                displayedConservativeGenericCarbon &&
                  classes.carbonOptionSelected,
              )}
              aria-pressed={displayedConservativeGenericCarbon}
              onClick={() => setConservativeGenericCarbon(true)}
            >
              <Icon category="ui" icon="genericCarbonConservative" size={14} />
              <Typography component="span">Conservative (+25%)</Typography>
            </Box>
          </Box>
        </Box>

        {context === 'project' && (
          <ProjectTargetField
            previewPreset={previewPreset}
            focusRequestId={focusTargetRequestId}
          />
        )}
      </Box>
    </Paper>
  );

  const card = (
    <Box
      ref={anchorRef}
      className={cx(
        classes.root,
        context === 'overview' && classes.overviewRoot,
      )}
      data-scope-overlay-open={panelOpen ? '' : undefined}
    >
      {!hideTrigger && (
        <Box className={classes.topRow}>
          <PresetDropdown
            truncate={context === 'overview'}
            label={
              isPlaceholder ? (
                <>
                  {scopeSummary.lifecycle}
                  {scopeSummary.sbef && (
                    <>
                      <DotSeparator />
                      {scopeSummary.sbef}
                    </>
                  )}
                  <DotSeparator />
                  <Box
                    component="span"
                    className={classes.triggerCarbonIcon}
                    aria-hidden
                  >
                    <Icon
                      category="ui"
                      icon={
                        conservativeGenericCarbon
                          ? 'genericCarbonConservative'
                          : 'genericCarbonTypical'
                      }
                      size={12}
                    />
                  </Box>
                </>
              ) : (
                triggerLabel
              )
            }
            open={panelOpen}
            onToggle={handleTogglePanel}
          />
        </Box>
      )}
      {panelOpen &&
        (portal ? (
          <Popper
            data-type="menu"
            role="dialog"
            aria-label="Calculation scope"
            open
            anchorEl={anchorRef.current}
            placement="bottom-start"
            modifiers={portalModifiers}
            sx={{
              width: 'max-content',
              maxWidth: 'calc(100vw - 32px)',
              zIndex: (theme) =>
                theme.zIndex.modal + (context === 'overview' ? 0 : -1),
            }}
          >
            {panel}
          </Popper>
        ) : (
          panel
        ))}
    </Box>
  );

  return card;
};
